import * as THREE from 'three';
import { EDDA } from '@somnhold/content';
import { WORLD_PER_HEX } from '../coords.ts';
import type { UnRender } from './ctx.ts';

/**
 * Ser Edda's own effects, all in cold white light (never gold): her oath's circle breathing on the ground, the light
 * that turns back on a hero who strikes her guard, judgement falling from the sky, the aegis, the Cold Light's
 * column, and her oath breaking — a shell of light round her while she kneels.
 */
const TAU = Math.PI * 2;
const GLOW = '#dfe8ff';
const ACCENT = '#7f9fff';
const PALE = '#ffffff';
const DARK = '#1a1d26';
const SMOKE = '#2a2e3a';
const q = new THREE.Vector3();
const PALE_C = new THREE.Color(PALE);

export const eddaFx: UnRender = {
  look: { glow: GLOW, accent: ACCENT, pale: PALE, dark: DARK, smoke: SMOKE },
  clips: { eddaJudgement: 'judgement', eddaAegis: 'aegis', eddaOath: 'oath' },
  frame: (c, s, e, p, _yaw, h) => {
    const u = e.un;
    if (!u) return;
    const beat = (s.tick + e.id) % 24;
    // The oath's circle: a faint ring breathing out to its edge (strike her, not those inside it).
    if (beat === 0) c.vfx.ring(p.clone().setY(p.y + 0.04), (EDDA.oath.radius / 1000) * WORLD_PER_HEX * 0.85, (EDDA.oath.radius / 1000) * WORLD_PER_HEX, ACCENT, 16, 0.22);
    // Her oath broken: a shell of light round her, motes rising, while nothing lands on her.
    if ((u.k[0] ?? 0) > 0) {
      c.glow(p.clone().setY(p.y + 0.08), PALE_C, 2.4, 0.5);
      if (beat % 4 === 0) c.vfx.ring(p.clone().setY(p.y + 0.06), 0.6, 1.1, PALE, 8, 0.7);
      if (beat % 2 === 0) {
        const a = Math.random() * TAU;
        c.vfx.burst(p.clone().add(q.set(Math.cos(a) * 0.6, h * 0.2, Math.sin(a) * 0.6)), { n: 1, color: PALE, speed: 0.004, up: 0.05, size: 0.1, life: 16, gravity: -0.004 });
      }
      return;
    }
    // Unstoppable after the aegis: a pale ring at her feet.
    if (u.unstoppable > 0 && beat % 6 === 0) c.vfx.ring(p.clone().setY(p.y + 0.05), 0.4, 0.8, ACCENT, 8, 0.5);
  },
  skill: (c, _s, _e, name, p, h) => {
    if (name !== 'eddaOath') return false;
    // The oath breaks: a column of white light falls on her and stays while she kneels.
    c.vfx.shaft(p, p.clone().setY(p.y + 7), PALE, 40, 0.9);
    c.vfx.ring(p.clone().setY(p.y + 0.06), 0.3, 3.2, PALE, 18, 1);
    c.vfx.ring(p.clone().setY(p.y + 0.1), 0.2, 2, ACCENT, 22, 0.8);
    c.vfx.burst(p.clone().setY(p.y + h * 0.6), { n: c.low ? 12 : 30, color: '#2c303c', speed: 0.12, up: 0.1, size: 0.15, life: 20, gravity: 0.012, spread: 0.3 });
    c.flare(p.clone().setY(p.y + h * 0.7), PALE, 6, 24);
    c.shake(0.5);
    return true;
  },
  beam: (c, kind, a, b) => {
    if (kind !== 'eddaOath') return false;
    // The blow on her guard comes back on the hero as light.
    c.vfx.beam(a, b, GLOW, 5, 0.025, 0.1);
    c.vfx.burst(b, { n: c.low ? 2 : 4, color: PALE, speed: 0.04, up: 0.03, size: 0.07, life: 8 });
    return true;
  },
  boom: (c, _s, e, kind, p, r) => {
    const low = c.low;
    // `r` comes in world units (converted again, every ring was the 0.8 floor).
    const R = r;
    if (kind === 'eddaJudgement') {
      // Cold light from the sky onto the hero.
      c.vfx.shaft(p.clone().setY(p.y + 6), p, PALE, 12, 0.5);
      c.vfx.beam(p.clone().setY(p.y + 6), p, ACCENT, 8, 0.05, 0);
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.15, R, PALE, 12, 1);
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 6 : 14, color: GLOW, speed: 0.08, up: 0.08, size: 0.09, life: 12, gravity: 0.008 });
      c.flare(p.clone().setY(p.y + 0.8), PALE, 2.5, 10);
      c.shake(0.15);
      return true;
    }
    if (kind === 'eddaAegis') {
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, R, ACCENT, 16, 0.9);
      c.vfx.ring(p.clone().setY(p.y + 0.08), 0.2, R * 0.55, PALE, 12, 0.8);
      c.vfx.implode(p.clone().setY(p.y + 1.2), R * 0.6, { n: low ? 6 : 16, color: PALE, size: 0.08, life: 14, swirl: 0.3, lift: 0.02 });
      c.flare(p.clone().setY(p.y + 1.4), GLOW, 3, 12);
      c.shake(0.15);
      return true;
    }
    if (kind !== 'eddaPillar') return false;
    // The Cold Light: a column of white light on the knot of heroes, a ring rolling out, shards of glass flying.
    if (e) c.force(e.id, 'pillar');
    c.vfx.shaft(p.clone().setY(p.y + 9), p, PALE, 22, Math.min(1.6, R * 0.6));
    c.vfx.shaft(p.clone().setY(p.y + 9), p, ACCENT, 26, Math.min(2.4, R * 0.9));
    c.vfx.ring(p.clone().setY(p.y + 0.06), 0.3, R, PALE, 16, 1);
    c.vfx.ring(p.clone().setY(p.y + 0.1), 0.2, R * 0.7, ACCENT, 20, 0.9);
    for (let i = 0; i < (low ? 5 : 9); i++) {
      const a = (i / (low ? 5 : 9)) * TAU;
      c.vfx.burst(p.clone().add(q.set(Math.cos(a) * R * 0.8, 0.3, Math.sin(a) * R * 0.8)), { n: low ? 2 : 4, color: SMOKE, speed: 0.04, up: 0.05, size: 0.7, life: 22, smoke: true, spread: 0.3 });
    }
    c.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 10 : 28, color: GLOW, speed: 0.12, up: 0.12, size: 0.11, life: 18, gravity: 0.008, spread: 0.6 });
    c.flare(p.clone().setY(p.y + 1.5), PALE, 8, 18);
    c.shake(0.9);
    return true;
  },
};
